import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import ChatbotPanel from './ChatbotPanel';
import { v3Api } from '../hooks/useApi';
import './chatbot-fab.css';

/** Modern "AI chat" glyph — a chat bubble with an AI sparkle. */
function AiChatIcon({ size = 24 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            {/* speech bubble */}
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
            {/* AI sparkle inside the bubble */}
            <path d="M11 6.7l1.05 2.7 2.7 1.05-2.7 1.05L11 14.2l-1.05-2.7L7.25 10.45l2.7-1.05z"
                fill="currentColor" stroke="none" />
            {/* small twinkle */}
            <circle cx="15.5" cy="7.1" r="0.85" fill="currentColor" stroke="none" />
        </svg>
    );
}

/**
 * Chatbot Launcher — Sprint 2 F3.
 *
 * Global FAB + Ctrl+K shortcut. Mount once at app shell level.
 */
export default function ChatbotLauncher() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    // null = still checking; false = disabled for this concessionnaire; true = show.
    // The backend (GET /chatbot/access) is the source of truth — the org-level
    // feature gate lives there. Fail-closed: any error keeps the launcher hidden
    // rather than showing a button whose every action would 403.
    const [enabled, setEnabled] = useState(null);

    useEffect(() => {
        let alive = true;
        v3Api.chatbot.access()
            .then(res => { if (alive) setEnabled(res?.data?.enabled === true); })
            .catch(() => { if (alive) setEnabled(false); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        function onKey(e) {
            if (!enabled) return; // ignore the Ctrl+K shortcut when gated off
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(o => !o);
            }
            if (e.key === 'Escape' && open) setOpen(false);
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, enabled]);

    // Hidden while the check is in flight, and whenever the concessionnaire does
    // not have the AI chatbot enabled. Coarse role/route suppression still lives
    // in Layout.jsx; this is the fine, backend-driven gate.
    if (!enabled) return null;

    return (
        <>
            <button
                className={`v3-chatbot-fab ${open ? 'v3-chatbot-fab--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                title={t('Ask AI (Ctrl+K)')}
                aria-label={open ? t('Close AI chatbot') : t('Open AI chatbot')}
            >
                <span className="v3-chatbot-fab__icon v3-chatbot-fab__icon--chat">
                    <AiChatIcon size={24} />
                </span>
                <span className="v3-chatbot-fab__icon v3-chatbot-fab__icon--close">
                    <X size={22} strokeWidth={2.5} />
                </span>
                <span className="v3-chatbot-fab__pulse" aria-hidden="true" />
            </button>
            <ChatbotPanel isOpen={open} onClose={() => setOpen(false)} />
        </>
    );
}
